import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import heroFallback from "../../../Assets/hero-banner-bg.png";
import "./HomeAboutSection.css";

const SHOWROOM_IMAGE =
  "https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?auto=format&fit=crop&w=1400&q=80";
const SHOWROOM_FALLBACK =
  "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?auto=format&fit=crop&w=1400&q=80";

const FAMILY_IMAGE =
  "https://images.unsplash.com/photo-1625047509248-ec889cbff17f?auto=format&fit=crop&w=900&q=80";
const FAMILY_FALLBACK =
  "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&w=900&q=80";

function AboutSectionImage({ src, fallback, alt }) {
  const [currentSrc, setCurrentSrc] = useState(src);

  const handleError = () => {
    setCurrentSrc((prev) => {
      if (prev === fallback) return heroFallback;
      return fallback;
    });
  };

  return (
    <img
      src={currentSrc}
      alt={alt}
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      onError={handleError}
    />
  );
}

const FEATURES = [
  {
    id: "verified",
    title: "Verified & Inspected Cars",
    description:
      "Every listed vehicle is carefully verified with detailed inspection reports, genuine ownership information, and transparent history so buyers can purchase with confidence.",
  },
  {
    id: "easy",
    title: "Easy Buying & Selling",
    description:
      "Buyers can compare vehicles, book test drives, and connect directly with trusted sellers, while sellers can easily list and manage their cars from one dashboard.",
  },
];

function GrowthIllustration() {
  return (
    <svg
      className="home-about__growth-art"
      viewBox="0 0 220 160"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M18 118h170"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M28 118c18-10 34-8 52-22 18-14 30-6 48-18 16-11 30-20 52-28"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M150 50l18 12-18 12"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect
        x="42"
        y="86"
        width="54"
        height="22"
        rx="6"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M50 98h12M66 98h8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="54" cy="108" r="4" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="82" cy="108" r="4" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M118 72c0-10 8-18 18-18s18 8 18 18"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M136 72v14l10 6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CircularBadge({ reduceMotion }) {
  const badgeMotion = reduceMotion
    ? {}
    : {
        animate: { y: [0, -8, 0] },
        transition: { duration: 4.5, repeat: Infinity, ease: "easeInOut" },
      };

  return (
    <motion.div className="home-about__badge" {...badgeMotion}>
      <svg viewBox="0 0 120 120" className="home-about__badge-ring" aria-hidden>
        <defs>
          <path
            id="home-about-badge-circle"
            d="M 60,60 m -44,0 a 44,44 0 1,1 88,0 a 44,44 0 1,1 -88,0"
          />
        </defs>
        <text className="home-about__badge-text">
          <textPath href="#home-about-badge-circle" startOffset="0%">
            Trusted Cars • Verified Sellers • Premium Quality •
          </textPath>
        </text>
      </svg>
      <span className="home-about__badge-icon" aria-hidden>
        <ArrowUpRight size={22} strokeWidth={2.2} />
      </span>
    </motion.div>
  );
}

export default function HomeAboutSection() {
  const reduceMotion = useReducedMotion();

  const imageHover = reduceMotion
    ? {}
    : {
        whileHover: { scale: 1.03 },
        transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
      };

  return (
    <section className="home-about" aria-labelledby="home-about-title">
      <div className="home-about__inner">
        <div className="home-about__media">
          <div className="home-about__accent-strip" aria-hidden />

          <motion.figure
            className="home-about__image home-about__image--main"
            {...imageHover}
          >
            <AboutSectionImage
              src={SHOWROOM_IMAGE}
              fallback={SHOWROOM_FALLBACK}
              alt="Customers inspecting premium used cars in a modern showroom"
            />
          </motion.figure>

          <motion.figure
            className="home-about__image home-about__image--overlap"
            {...imageHover}
          >
            <AboutSectionImage
              src={FAMILY_IMAGE}
              fallback={FAMILY_FALLBACK}
              alt="Happy family celebrating their newly purchased used car"
            />
          </motion.figure>
        </div>

        <div className="home-about__content">
          <CircularBadge reduceMotion={reduceMotion} />

          <p className="home-about__label">About CarsInfinite</p>

          <h2 id="home-about-title" className="home-about__title">
            India&apos;s Trusted Marketplace for Quality Used Cars
          </h2>

          <p className="home-about__lead">
            We help buyers find inspected, verified, and affordable used cars from
            trusted sellers across India — with transparent pricing, genuine
            listings, and support at every step.
          </p>

          <div className="home-about__features">
            {FEATURES.map((feature) => (
              <article key={feature.id} className="home-about__feature">
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </article>
            ))}
          </div>

          <GrowthIllustration />
        </div>
      </div>
    </section>
  );
}
